import { useEffect, useState, useCallback, useRef } from 'react';

const VERSION_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateRequired, setUpdateRequired] = useState(false);
  const [updateType, setUpdateType] = useState('REQUIRED');
  const [timeWindow, setTimeWindow] = useState(0);
  const [latestVersion, setLatestVersion] = useState(null);
  const [latestBuildNumber, setLatestBuildNumber] = useState(null);
  const [features, setFeatures] = useState([]);
  const [checking, setChecking] = useState(false);
  const isCheckingRef = useRef(false);

  const currentVersion = window.__APP_VERSION_INFO?.version ?? '1.0.0';
  const currentBuildNumber = window.__APP_VERSION_INFO?.buildNumber ?? 'unknown';

  const checkVersion = useCallback(async () => {
    if (isCheckingRef.current) return;
    
    isCheckingRef.current = true;
    setChecking(true);
    try {
      // Add cache-busting timestamp to force fetch from server
      const timestamp = Date.now();
      const response = await fetch(`/app-version.json?t=${timestamp}`, { 
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch version file: ${response.status} ${response.statusText}`);
      }
      
      const versionData = await response.json();
      const latestVer = versionData.version;
      const latestBuild = versionData.buildNumber;
      
      if (!latestVer) {
        throw new Error('Invalid version data: missing version field');
      }
      
      const isUpdateAvailable = latestVer !== currentVersion || latestBuild !== currentBuildNumber;
      
      setUpdateAvailable(isUpdateAvailable);
      setUpdateRequired(true);
      setUpdateType('REQUIRED');
      setTimeWindow(0);
      setLatestVersion(latestVer);
      setLatestBuildNumber(latestBuild);
      setFeatures(versionData.features || []);
    } catch (error) {
      // On error, assume no update needed
      setUpdateAvailable(false);
      setLatestVersion(currentVersion);
      setLatestBuildNumber(currentBuildNumber);
    } finally {
      isCheckingRef.current = false;
      setChecking(false);
    }
  }, [currentVersion, currentBuildNumber]);

  useEffect(() => {
    // Initial check
    checkVersion();

    // Regular interval checks
    const regularInterval = setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS);

    // Listen for hard refresh events
    const handleHardRefresh = () => {
      console.log('Hard refresh detected, forcing version check');
      checkVersion();
    };

    window.addEventListener('hardRefresh', handleHardRefresh);

    return () => {
      clearInterval(regularInterval);
      window.removeEventListener('hardRefresh', handleHardRefresh);
    };
  }, [checkVersion]);

  const forceUpdate = async () => {
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Clear service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }

      // Clear version-related localStorage
      localStorage.removeItem('latestAppVersion');
      localStorage.removeItem('lastVersionCheck');
      localStorage.removeItem('updateDismissed');

      // Hard reload with cache busting
      const timestamp = Date.now();
      window.location.href = `${window.location.pathname}?t=${timestamp}`;
    } catch (error) {
      console.warn('Cache cleanup warning:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  };

  const dismissUpdate = () => {
    // All updates are mandatory - cannot dismiss
    console.warn('Cannot dismiss mandatory updates');
    return false;
  };

  return { 
    updateAvailable, 
    updateRequired,
    updateType,
    timeWindow,
    latestVersion,
    latestBuildNumber,
    features,
    checking,
    forceUpdate,
    dismissUpdate,
    currentVersion,
    currentBuildNumber
  };
};
